'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Logique commune des carrousels « coverflow » (accueil musique + accueil vidéo) :
 * position flottante, glisser AU DOIGT avec un peu d'élan (plus de glisser à la
 * souris : peu fiable), défilement automatique, clic sur une carte (latérale →
 * centrée ; centrale → onActivate).
 * PILOTAGE SOURIS : double-clic sur une flèche → le carrousel tourne selon la
 * position du curseur (à droite → vers la droite, à gauche → vers la gauche,
 * plus on s'éloigne du centre plus c'est rapide). Double-clic à nouveau ou Échap
 * pour quitter.
 * Les cartes portent `data-i={index}`.
 */
export function wrap(o: number, n: number) {
  o = ((o % n) + n) % n
  return o > n / 2 ? o - n : o
}

export function useCoverflow({ n, gap, intervalMs = 5000, start = 0, resetKey, onActivate, activateOnSide = false }: {
  n: number
  gap: number
  intervalMs?: number
  start?: number
  resetKey?: unknown
  onActivate: (index: number) => void
  /** true : un clic sur une carte LATÉRALE la centre ET la lance (lecture en un clic). */
  activateOnSide?: boolean
}) {
  const [pos, setPos] = useState(start)
  const [dragging, setDragging] = useState(false)
  const posRef = useRef(start)
  posRef.current = pos
  const activateRef = useRef(onActivate)
  activateRef.current = onActivate
  const drag = useRef<{ x: number; start: number; moved: boolean; t: number; idx: number | null; mouse: boolean } | null>(null)
  const autoRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => { setPos(Math.min(start, Math.max(0, n - 1))) }, [resetKey, n, start])

  const restartAuto = useCallback(() => {
    if (autoRef.current) clearInterval(autoRef.current)
    if (steer.current.on) return   // pas de défilement auto pendant le pilotage souris
    autoRef.current = setInterval(() => setPos(p => Math.round(p) + 1), intervalMs)
  }, [intervalMs])
  useEffect(() => { restartAuto(); return () => { if (autoRef.current) clearInterval(autoRef.current) } }, [restartAuto])

  const goTo = useCallback((p: number) => { setPos(p); restartAuto() }, [restartAuto])
  const active = n ? ((Math.round(pos) % n) + n) % n : 0

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('[data-nodrag]')) return
    const card = (e.target as HTMLElement).closest('[data-i]') as HTMLElement | null
    // Souris : clic seulement (moved ne passe jamais à true) ; doigt : glisser.
    drag.current = { x: e.clientX, start: posRef.current, moved: false, t: performance.now(), idx: card ? Number(card.dataset.i) : null, mouse: e.pointerType === 'mouse' }
    if (autoRef.current) clearInterval(autoRef.current)
  }

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const d = drag.current; if (!d || d.mouse) return
      const dx = e.clientX - d.x
      if (!d.moved && Math.abs(dx) > 5) { d.moved = true; setDragging(true) }
      if (d.moved) setPos(d.start - dx / gap)
    }
    const up = (e: PointerEvent) => {
      const d = drag.current; if (!d) return
      drag.current = null
      setDragging(false)
      if (d.moved) {
        const v = (e.clientX - d.x) / Math.max(1, performance.now() - d.t)
        setPos(p => Math.round(p - Math.max(-2, Math.min(2, v * 1.2))))
      } else if (d.idx != null && n) {
        const p = posRef.current
        const act = ((Math.round(p) % n) + n) % n
        if (d.idx !== act) { setPos(Math.round(p) + wrap(d.idx - p, n)); if (activateOnSide) activateRef.current(d.idx) }
        else activateRef.current(d.idx)
      }
      restartAuto()
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
  }, [n, gap, restartAuto])

  // --- TÉLÉCOMMANDE (TV) : TvMode envoie « blc-tv-hkey » (gauche/droite) aux
  // éléments [data-tvhkeys] ; on les consomme → carte précédente/suivante au lieu
  // de sauter ailleurs dans la page. OK (clic clavier, detail 0) = lire le centre.
  const activeRef = useRef(0)
  activeRef.current = active
  const goToRef = useRef(goTo)
  goToRef.current = goTo
  // L'écouteur est posé DÈS que l'élément apparaît (ref de rappel) : certains
  // carrousels ne s'affichent qu'une fois leurs titres chargés.
  const tvEl = useRef<HTMLElement | null>(null)
  const onHKey = useRef((e: Event) => {
    const d = (e as CustomEvent).detail
    e.preventDefault()
    goToRef.current(Math.round(posRef.current) + (d === 'left' ? -1 : 1))
  }).current
  // --- PILOTAGE SOURIS (double-clic sur une flèche) ---
  const steer = useRef({ on: false, v: 0, raf: 0, last: 0 })
  const steerStop = useCallback((snap = true) => {
    const s = steer.current
    s.v = 0
    if (s.raf) { cancelAnimationFrame(s.raf); s.raf = 0 }
    if (snap) { setDragging(false); setPos(p => Math.round(p)) }
  }, [])
  const steerTick = useCallback((t: number) => {
    const s = steer.current
    const dt = Math.min(64, t - (s.last || t)); s.last = t
    if (!s.on || s.v === 0) { s.raf = 0; return }
    setPos(p => p + s.v * dt / 1000)
    s.raf = requestAnimationFrame(steerTick)
  }, [])
  const setSteer = useCallback((on: boolean) => {
    const s = steer.current
    s.on = on
    if (tvEl.current) { if (on) tvEl.current.setAttribute('data-steer', ''); else tvEl.current.removeAttribute('data-steer') }
    if (on) { if (autoRef.current) clearInterval(autoRef.current) }
    else { steerStop(); restartAuto() }
  }, [steerStop, restartAuto])
  const listeners = useRef<{ move: (e: MouseEvent) => void; leave: () => void; dbl: (e: MouseEvent) => void; key: (e: KeyboardEvent) => void; nodrag: (e: Event) => void } | null>(null)
  if (!listeners.current) listeners.current = {
    move: (e) => {
      const s = steer.current, el = tvEl.current
      if (!s.on || !el) return
      const r = el.getBoundingClientRect()
      const x = ((e.clientX - r.left) / r.width) * 2 - 1          // -1 (gauche) … 1 (droite)
      const dead = 0.18
      const k = Math.abs(x) < dead ? 0 : Math.sign(x) * ((Math.abs(x) - dead) / (1 - dead))
      const v = k * 3.2                                             // jusqu'à ~3 cartes / s
      if (v === 0) { if (s.v !== 0) steerStop(); return }
      if (s.v === 0) { setDragging(true); s.last = 0 }
      s.v = v
      if (!s.raf) s.raf = requestAnimationFrame(steerTick)
    },
    leave: () => { if (steer.current.v !== 0) steerStop() },
    dbl: (e) => {
      const b = (e.target as HTMLElement)?.closest?.('button[aria-label="Précédent"], button[aria-label="Suivant"]')
      if (!b) return
      e.preventDefault()
      setSteerRef.current(!steer.current.on)
    },
    key: (e) => { if (e.key === 'Escape' && steer.current.on) setSteerRef.current(false) },
    // Jamais de glisser natif d'image/lien dans le carrousel.
    nodrag: (e) => e.preventDefault(),
  }
  const setSteerRef = useRef(setSteer)
  setSteerRef.current = setSteer
  useEffect(() => {
    const k = listeners.current!.key
    window.addEventListener('keydown', k)
    return () => { window.removeEventListener('keydown', k); steerStop(false) }
  }, [steerStop])

  const tvRef = useCallback((el: HTMLElement | null) => {
    const L = listeners.current!
    if (tvEl.current) {
      tvEl.current.removeEventListener('blc-tv-hkey', onHKey)
      tvEl.current.removeEventListener('mousemove', L.move)
      tvEl.current.removeEventListener('mouseleave', L.leave)
      tvEl.current.removeEventListener('dblclick', L.dbl)
      tvEl.current.removeEventListener('dragstart', L.nodrag)
    }
    tvEl.current = el
    if (el) {
      el.addEventListener('blc-tv-hkey', onHKey)
      el.addEventListener('mousemove', L.move)
      el.addEventListener('mouseleave', L.leave)
      el.addEventListener('dblclick', L.dbl)
      el.addEventListener('dragstart', L.nodrag)
      if (steer.current.on) el.setAttribute('data-steer', '')
    }
  }, [onHKey])
  const tvProps = {
    ref: tvRef,
    'data-tvhkeys': '',
    onClick: (e: React.MouseEvent) => { if (e.detail === 0 && n) activateRef.current(activeRef.current) },
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') goTo(Math.round(posRef.current) + 1)
    if (e.key === 'ArrowLeft') goTo(Math.round(posRef.current) - 1)
  }

  return {
    pos, active, dragging, onPointerDown, onKeyDown, restartAuto, tvProps,
    next: () => goTo(Math.round(posRef.current) + 1),
    prev: () => goTo(Math.round(posRef.current) - 1),
    goToIndex: (i: number) => goTo(Math.round(posRef.current) + wrap(i - posRef.current, n)),
    /** Décalage signé de la carte i par rapport au centre (fractionnaire pendant le glisser). */
    offset: (i: number) => wrap(i - pos, n),
  }
}
